<style lang="scss">
  @import "./configuration";
</style>

<script>
import ConfigurationService from './configuration.service'
import { CONSTANT } from '../../utils/constant.js'
import { mapGetters } from 'vuex'
import sharedService from '../../utils/shared-service.js'
import traceService from '../shared/trace/trace-service.js'
import toasterService from '../../utils/toaster.js'
import messages from '../../messages.js'
import jwtVerification from '../../utils/jwt-verification'

const configurationService = new ConfigurationService()

export default {
  name: 'Configuration',

  data () {
    return {
      configurationManager: configurationService,
      CONSTANT: CONSTANT,
      environment: CONSTANT.CONFIG.ENVIRONMENT.SANDBOX,
      currentTab: CONSTANT.CONFIG.TITLE.CHECKOUT,
      confirmationRequest: {},
      enableAdvance: '',
      jwtExpiryTime: '',
      currentTime: ''
    }
  },

  computed: {
    ...mapGetters(['advanced', 'checkout', 'isSRCCheckoutDisabled', 'srcCorrelationId', 'enableConfirmation', 'allEnvironments', 'allSdkLoaded', 'c2pSelectedCurrency', 'region', 'toggleMacsFeaturesList'])
  },

  watch: {
    $route () {
      this.setCurrentTab()
    },
    toggleMacsFeaturesList () {
      const isNTSEnabled = sharedService.checkFeatureFlag('START_NTS_CHANGES')
      if (!isNTSEnabled) {
        this.$store.commit('setEnableNativeIntegrationFlow', isNTSEnabled)
      }
      const isMPTEnabled = sharedService.checkFeatureFlag('START_MPT_CHANGES')
      if (!isMPTEnabled) {
        this.$store.dispatch('updateMPTFlow', isMPTEnabled)
      }
    }
  },

  created () {
    this.$store.commit('updateIsSRCCheckoutDisabled', true)
    this.environment = localStorage.getItem('setEnv') || configurationService.getConfigDataOnEnvironment()
    this.setCurrentTab()
    setInterval(this.updateTime, 1000)
    this.updateTime()
  },

  methods: {
    updateTime () {
      let date = new Date()
      this.currentTime = Math.floor(date.getTime() / 1000.0)
      this.checkUserAccessToken()
    },

    checkUserAccessToken () {
      this.jwtExpiryTime = jwtVerification.validateJwtExpiry()
      if (this.$store.state.configuration.userAccessToken !== '' && this.currentTime < this.jwtExpiryTime) {
        this.enableAdvance = true
      } else {
        this.enableAdvance = false
        this.$store.dispatch('updateUserAccessToken', '')
        this.$store.dispatch('adminUserName', null)
        const configDetails = JSON.parse(localStorage.getItem('configDetails'))
        if (configDetails) {
          configDetails.userAccessToken = ''
          configDetails.adminUserName = null
          localStorage.setItem('configDetails', JSON.stringify(configDetails))
        }
      }
    },

    setCurrentTab () {
      let currentRouteName = this.$router.currentRoute.name || ''
      if (currentRouteName.toLowerCase() === CONSTANT.CONFIG.TITLE.CONFIGURATION) {
        this.currentTab = CONSTANT.CONFIG.TITLE.CHECKOUT
        this.$router.push('/configuration/checkout').catch(err => { console.error(err) })
      } else {
        this.currentTab = currentRouteName.toLowerCase()
      }
    },

    loadCurrentTab (clickedTab) {
      this.currentTab = clickedTab.toLowerCase()
      this.$router.push('/configuration/' + clickedTab.toLowerCase()).catch(err => { console.error(err) })
    },

    /**
     * reset all selected config
     */
    resetConfiguration () {
      this.$store.dispatch('resetConfig').then(() => {
        localStorage.setItem('setEnv', CONSTANT.CONFIG.ENVIRONMENT.SANDBOX)
        window.location.reload()
      })
    },

    /**
     * Load config based on environment selection
     * @param environmentId
     */
    loadEnvironmentConfigurations (environmentId) {
      if (environmentId !== CONSTANT.CONFIG.ENVIRONMENT.SANDBOX) {
        this.$store.commit('setRegion', CONSTANT.REGION.GLOBAL)
        this.$store.commit('changeCurrency', this.c2pSelectedCurrency)
      } else {
        this.region === CONSTANT.REGION.GLOBAL ? this.$store.commit('changeCurrency', this.c2pSelectedCurrency) : this.$store.commit('changeCurrency', CONSTANT.SCOF_CURRENCY[this.region])
      }
      this.$store.dispatch('updateEnvironment', environmentId).then((env) => {
        localStorage.setItem('setEnv', env)
        window.location.reload()
      })
    },

    /**
     * Initiate srci
     */
    openSrc () {
      sharedService.invokeSrc(this.$router, CONSTANT.SRC.CONFIGURATION, this.$store)
    },

    /**
     * Initiate init/ isRecognize flow
     */
    async initSrc () {
      this.region === CONSTANT.REGION.GLOBAL ? this.$store.commit('changeCurrency', this.c2pSelectedCurrency) : this.$store.commit('changeCurrency', CONSTANT.SCOF_CURRENCY[this.region])
      try {
        this.$store.commit('updateSRCTotalPrice', CONSTANT.SRC.CONFIGURATION)
        this.$store.commit('lightBoxloaderOn', true)
        sharedService.intializeSdk().then(() => {
          sharedService.enableSRCCheckOut()
        })
      } catch (e) {
        console.error('Error occurs while calling pre checkout', e)
        await this.$store.dispatch('lightBoxloaderOn', false)
      }
    },

    /**
     * Call Confirmation API.
     */
    async postConfirmation () {
      let srcNetwork = configurationService.getSrcNetwork()
      sharedService.disableConfirmationButton()
      sharedService.confirmation(this.srcCorrelationId, srcNetwork).then(response => {
        traceService.updateCallTraces(srcNetwork, response.callTraces, true)
        toasterService.onSuccess(messages['en-US'].label.confirmationSuccess)
      }).catch(error => {
        sharedService.confirmationData(this.srcCorrelationId, srcNetwork).then(
          response => {
            this.confirmationRequest = response
            let errorLog = error.response.data.callTraces || sharedService.serviceDownError(srcNetwork, error, this.confirmationRequest, messages['en-US'].errorMessages.SERVICE_DOWN_ERROR.CONFIRMATION)
            traceService.updateCallTraces(srcNetwork, errorLog, false)
            toasterService.onFailure(messages['en-US'].errorMessages.CONFIRMATION_ERROR)
          })
      })
    }
  }
}
</script>

<template>
  <div class="configuration-page">
    <div
      id="configuration"
      class="container"
    >
      <div class="row">
        <div class="col-8">
          <h3 class="mt-4">
            {{ $t ('label.configurationConsole') }}
          </h3>
        </div>
        <div class="col-4">
          <button
            id="config_reset_button"
            class="pull-right mt-4 btn btn-primary btn-lg"
            @click="resetConfiguration()"
          >
            Reset
          </button>
          <a
            aria-controls="collapseExample"
            aria-expanded="false"
            class="pull-right mt-4 btn border-dark mr-1 d-md-none"
            data-toggle="collapse"
            href="#collapseExample"
            role="button"
          >
            <span class="fa fa-bars" />
          </a>
        </div>
      </div>
      <div class="border-dark border-top my-2" />
      <div class="row">
        <div class="col-md-4 mt-3">
          <div
            id="collapseExample"
            class="collapse d-md-block"
          >
            <div class="menu greylr">
              <div>
                <select
                  id="select_environment"
                  v-model="environment"
                  class="env-dropdown"
                  @change="loadEnvironmentConfigurations(environment)"
                >
                  <option
                    v-for="env in allEnvironments"
                    :key="env"
                    :value="env"
                  >
                    {{ env }}
                  </option>
                </select>
              </div>

              <div
                id="config-checkout-tab"
                :class="[{'greyrl' : currentTab === CONSTANT.CONFIG.TITLE.CHECKOUT}]"
                @click="loadCurrentTab(CONSTANT.CONFIG.TITLE.CHECKOUT)"
              >
                {{ $t ('label.checkout') }}
                <div>
                  <small class="form-text text-muted small-font">{{ $t ('configTabLabels.checkout') }}
                  </small>
                </div>
              </div>

              <div
                id="auth-sdk-tab"
                :class="[{'greyrl' : currentTab === CONSTANT.CONFIG.TITLE.AUTH}]"
                @click="loadCurrentTab(CONSTANT.CONFIG.TITLE.AUTH)"
              >
                {{ $t ('label.auth') }}
                <div>
                  <small class="form-text text-muted small-font">{{ $t ('configTabLabels.auth') }}
                  </small>
                </div>
              </div>

              <div
                id="config-callback-tab"
                :class="[{'greyrl' : currentTab === CONSTANT.CONFIG.TITLE.CALLBACK}]"
                @click="loadCurrentTab(CONSTANT.CONFIG.TITLE.CALLBACK)"
              >
                {{ $t ('label.callback') }}
                <div>
                  <small class="form-text text-muted small-font">{{ $t ('configTabLabels.callback') }}
                  </small>
                </div>
              </div>

              <div
                v-if="enableAdvance"
                id="config-advanced-tab"
                :class="[{'greyrl' : currentTab === CONSTANT.CONFIG.TITLE.ADVANCED}]"
                @click="loadCurrentTab(CONSTANT.CONFIG.TITLE.ADVANCED)"
              >
                {{ $t ('label.advance') }}
                <div>
                  <small class="form-text text-muted small-font">{{ $t ('configTabLabels.advance') }}
                  </small>
                </div>
              </div>

              <div
                id="config-federatedID-tab"
                :class="[{'greyrl' : currentTab === CONSTANT.CONFIG.TITLE.FEDERATEDID}]"
                @click="loadCurrentTab(CONSTANT.CONFIG.TITLE.FEDERATEDID)"
              >
                {{ $t ('label.federatedID') }}
                <div>
                  <small class="form-text text-muted small-font">{{ $t ('configTabLabels.federatedId') }}
                  </small>
                </div>
              </div>
              <div
                id="config-tokenization-tab"
                :class="[{'greyrl' : currentTab === CONSTANT.CONFIG.TITLE.TOKENIZATION}]"
                @click="loadCurrentTab(CONSTANT.CONFIG.TITLE.TOKENIZATION)"
              >
                {{ $t ('label.tokenization') }}
                <div>
                  <small class="form-text text-muted small-font">{{ $t ('configTabLabels.tokenization') }}
                  </small>
                </div>
              </div>

              <div
                id="config-storefront-tab"
                :class="[{'greyrl' : currentTab === CONSTANT.CONFIG.TITLE.STOREFRONT}]"
                @click="loadCurrentTab(CONSTANT.CONFIG.TITLE.STOREFRONT)"
              >
                {{ $t ('label.storefront') }}
                <div>
                  <small class="form-text text-muted small-font">{{ $t ('configTabLabels.storefront') }}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>

        <transition
          mode="out-in"
          name="component-fade"
        >
          <router-view
            ref="checkoutPage"
            class="col-md-8 mt-3 mb-5"
          />
        </transition>
      </div>
    </div>

    <div class="config-footer">
      <div class="container">
        <div class="row no-gutters border m-0 p-2">
          <div class="col-sm-12">
            <p><strong>{{ $t ('description.note') }}</strong></p>
            <p>{{ $t ('description.footerDescription') }}</p>
          </div>
          <div
            id="precheckout-footer-btn"
            class="col-lg-4 col-md-4 col-xl-4 pr-md-2"
          >
            <label>{{ $t ('description.preCheckout') }}</label>
            <button
              id="precheckout-btn"
              :disabled="!allSdkLoaded"
              class="btn btn-dark rounded-pill px-4 py-2"
              data-cy="precheckout-button"
              type="button"
              @click="initSrc"
            >
              {{ $t ('label.preCheckout') }}
            </button>
          </div>
          <div class="col-lg-4 col-md-4 col-xl-5">
            <div id="src-footer-btn">
              <label
                v-if="isSRCCheckoutDisabled"
              >{{ $t ('description.srcCheckout') }}</label>
              <button
                id="src-btn"
                :disabled="isSRCCheckoutDisabled"
                :class="[ !isSRCCheckoutDisabled ? 'mt-lg-5 mt-src-button' : '' ]"
                data-cy="src-button"
                class="btn rounded-pill p-0"
                type="button"
                @click="openSrc"
              >
                <img
                  :src="require('@/assets/images/src_checkout.png')"
                  class="config-src-btn"
                >
              </button>
            </div>
          </div>
          <div
            id="confirmation-footer-btn"
            class="col-lg-3 col-md-3 col-xl-3 pl-md-3"
          >
            <label>{{ $t ('description.confirmation') }}</label>
            <button
              id="confirmation-btn"
              :disabled="!enableConfirmation"
              class="btn btn-dark rounded-pill px-4 py-2"
              data-cy="confirmation-button"
              type="button"
              @click="postConfirmation"
            >
              {{ $t ('label.confirmation') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
